import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import { MdOutlineSystemUpdateAlt } from 'react-icons/md';
import { orderBy as lodashOrderBy } from 'lodash';

// material
import {
  Box,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Tooltip,
  Typography,
  Grid,
  TableRow,
  Button
} from '@mui/material';
import Scrollbar from '../../../components/Scrollbar';
import SearchNotFound from '../../../components/SearchNotFound';
import SimplePagination from '../../../components/SimplePagination';
import { UserListHead } from '../../../components/_dashboard/user';
import { RootStyle, SearchStyle, StyledTableRow, StyledTableCell } from '../../Global/Styling';

// ── Table columns ─────────────────────────────────────────────────────
const TABLE_HEAD = [
  { id: 'expenseDate', label: 'Expense Date' },
  { id: 'type', label: 'Type' },
  { id: 'fileRef', label: 'File No / Ref' },
  { id: 'reference', label: 'Reference' },
  { id: 'category', label: 'Category' },
  { id: 'amount', label: 'Amount (₹)' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'ACTION' }
];

// ── Filter options ─────────────────────────────────────────────────────
const CATEGORY_OPTIONS = [
  'Processing Fee',
  'Legal Fee',
  'Valuation Charges',
  'Admin Expenses',
  'Office Expenses',
  'Software Subscription',
  'Bank Charges',
  'Miscellaneous'
];
const STATUS_OPTIONS = ['Pending', 'Approved', 'Rejected', 'Paid'];

// ── Mock Data ─────────────────────────────────────────────────────────
const MOCK_EXPENSES = [
  {
    id: 1,
    expenseDate: '2024-02-15',
    type: 'Offline',
    fileRef: 'EXT-1001',
    reference: 'Office Rent',
    category: 'Admin Expenses',
    amount: 45000,
    status: 'Paid',
    remarks: 'Monthly office rent for Feb 24.'
  },
  {
    id: 2,
    expenseDate: '2024-02-18',
    type: 'Online',
    fileRef: 'EXT-1002',
    reference: 'Broadband',
    category: 'Office Expenses',
    amount: 2500,
    status: 'Approved',
    remarks: 'ACT Fiber net bill.'
  },
  {
    id: 3,
    expenseDate: '2024-02-20',
    type: 'Manual',
    fileRef: 'EXT-1003',
    reference: 'Legal Consultancy',
    category: 'Legal Fee',
    amount: 15000,
    status: 'Pending',
    remarks: 'Consultation for vendor agreement.'
  },
  {
    id: 4,
    expenseDate: '2024-02-22',
    type: 'Online',
    fileRef: 'EXT-1004',
    reference: 'AWS Cloud',
    category: 'Software Subscription',
    amount: 8200,
    status: 'Paid',
    remarks: 'Server hosting charges.'
  },
  {
    id: 5,
    expenseDate: '2024-02-23',
    type: 'Offline',
    fileRef: 'EXT-1005',
    reference: 'Tea & Snacks',
    category: 'Miscellaneous',
    amount: 1200,
    status: 'Rejected',
    remarks: 'Personal expenses not allowed.'
  }
];

const getStatusLabelColor = (s) =>
  ({
    Paid: 'success',
    Approved: 'success',
    Pending: 'warning',
    Rejected: 'error'
  }[s] || 'warning');

export default function Expense() {
  const navigate = useNavigate();

  const [filterName, setFilterName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(' ');
  const [selectedStatus, setSelectedStatus] = useState(' ');
  const [isOpenFilter, setOpenFilter] = useState(false);

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('expenseDate');
  const [exportAnchor, setExportAnchor] = useState(null);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  // ── Local Filtering & Sorting Logic ──
  const sortedAndFilteredExpenses = useMemo(() => {
    let data = [...MOCK_EXPENSES];

    // 1. Filter
    if (filterName) {
      data = data.filter(
        (row) =>
          row.reference.toLowerCase().includes(filterName.toLowerCase()) ||
          row.fileRef.toLowerCase().includes(filterName.toLowerCase())
      );
    }
    if (selectedCategory !== ' ') {
      data = data.filter((row) => row.category === selectedCategory);
    }
    if (selectedStatus !== ' ') {
      data = data.filter((row) => row.status === selectedStatus);
    }

    // 2. Sort
    return lodashOrderBy(data, [orderBy], [order]);
  }, [filterName, selectedCategory, selectedStatus, order, orderBy]);

  const isNotFound = sortedAndFilteredExpenses.length === 0;

  return (
    <Box>
      <Card>
        {/* ── Filter Bar ── */}
        {isOpenFilter && (
          <Box px={3} pt={2} pb={2} sx={{ borderBottom: '1px solid #eaedf1' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <MenuItem value=" ">Select Category</MenuItem>
                  {CATEGORY_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <MenuItem value=" ">Select Status</MenuItem>
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs="auto">
                <Tooltip title="Reset Filter">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedCategory(' ');
                      setSelectedStatus(' ');
                      setFilterName('');
                    }}
                  >
                    <FilterListOffIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ── Toolbar ── */}
        <RootStyle sx={{ minHeight: 45, borderBottom: '1px solid #eaedf1' }}>
          {/* LEFT — Export icon + Add Expense icon */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Export */}
            <Tooltip title="Export data" placement="top" arrow>
              <IconButton
                className="squareIconButton"
                onClick={(e) => setExportAnchor(e.currentTarget)}
              >
                <MdOutlineSystemUpdateAlt size={18} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
              PaperProps={{
                sx: { borderRadius: 1, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }
              }}
            >
              <MenuItem
                onClick={() => {
                  setExportAnchor(null);
                  // console.log('Export PDF');
                }}
                sx={{ fontSize: 13 }}
              >
                <PictureAsPdfIcon sx={{ fontSize: 16, mr: 1, color: '#dc2626' }} /> Export as PDF
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setExportAnchor(null);
                  // console.log('Export Excel');
                }}
                sx={{ fontSize: 13 }}
              >
                <GridOnIcon sx={{ fontSize: 16, mr: 1, color: '#16a34a' }} /> Export as Excel
              </MenuItem>
            </Menu>

            {/* Add Expense — icon only */}
            <Tooltip title="Add Expense" placement="top" arrow>
              <IconButton
                className="squareIconButton"
                onClick={() => navigate('/accounting/expense/add')}
              >
                <AddIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* RIGHT — Search + Filter toggle */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <SearchStyle
              value={filterName}
              onChange={handleFilterByName}
              placeholder="Search file no, reference…"
              startAdornment={
                <InputAdornment position="start">
                  <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
            />
            <Tooltip title="Filter list">
              <IconButton className="squareIconButton" onClick={() => setOpenFilter(!isOpenFilter)}>
                <Icon icon={roundFilterList} />
              </IconButton>
            </Tooltip>
          </Stack>
        </RootStyle>

        {/* ── Table ── */}
        <Scrollbar>
          <TableContainer sx={{ minWidth: 800, height: '50vh', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={sortedAndFilteredExpenses.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {sortedAndFilteredExpenses.map((row) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell>{row.expenseDate}</StyledTableCell>
                    <StyledTableCell>{row.type}</StyledTableCell>
                    <StyledTableCell>{row.fileRef}</StyledTableCell>
                    <StyledTableCell>{row.reference}</StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={row.category}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11 }}
                      />
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 600 }}>
                      ₹ {row.amount.toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        color={getStatusLabelColor(row.status)}
                        variant="ghost"
                      />
                    </StyledTableCell>

                    {/* ── Actions (mirroring PartnersList icon style) ── */}
                    <StyledTableCell width={90}>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            className="squareIconButton"
                            onClick={() => navigate(`/accounting/expense/view/${row.id}`)}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            className="squareIconButton"
                            onClick={() => navigate(`/accounting/expense/edit/${row.id}`)}
                          >
                            <EditOutlinedIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
              {isNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>

        <SimplePagination page={page} setPage={setPage} hasNextPage={false} />
      </Card>
    </Box>
  );
}
